import React, { useState, useEffect } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    faCheck, 
    faTimes, 
    faExclamationTriangle, 
    faSave,
    faArrowLeft,
    faUser,
    faMoneyBillWave,
    faCalendar,
    faFileAlt,
    faUpload,
    faDownload,
    faTrash,
    faSpinner,
    faFilePdf,
    faFileWord,
    faImage,
    faFileExcel
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Api from '../../Api';
import SuccessModal from '../../components/SuccessModal';

const PaymentConfirmation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    
    const [paymentBatch, setPaymentBatch] = useState(null);
    const [paymentLines, setPaymentLines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState({});
    const [proofFiles, setProofFiles] = useState({});
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const showDeleteConfirmationModalDismiss = useModalDismiss(() => setShowDeleteConfirmationModal(false), { backdropClose: false, enabled: showDeleteConfirmationModal });
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [savingProgress, setSavingProgress] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        fetchPaymentBatch();
    }, [id]);

    // Warn on tab close / navigation away when there are unsaved confirmation choices.
    useEffect(() => {
        const handler = (e) => {
            if (hasUnsavedChanges && paymentBatch?.status !== 'confirmed') {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedChanges, paymentBatch]);


    // Sum of |amount| for adjustments with type === 'survey_exclusion' (partial exclusion)
    const getExcludedSurveyAmountFromLine = (line) => {
        if (!line.adjustments || !Array.isArray(line.adjustments)) return 0;
        return line.adjustments
            .filter(adj => adj.type === 'survey_exclusion')
            .reduce((sum, adj) => sum + Math.abs(parseFloat(adj.amount) || 0), 0);
    };

    const fetchPaymentBatch = async () => {
        try {
            setLoading(true);
            const response = await Api.get(`/payments/${id}`);
            
            if (response.data.success) {
                const batch = response.data.data;
                setPaymentBatch(batch);
                
                // Filter out lines with 0 net amount (no payment needed, will be auto-marked as paid)
                // Keep negative-net lines (deductions to recover) and excluded lines — shown locked as unpaid
                const filteredLines = (batch.lines || []).filter(line => {
                    // Always include excluded lines (shown like <200: notes prefilled, status unpaid)
                    if (line.payment_status === 'excluded') return true;

                    // Always include negative-net lines (deductions exceeded balance — need tracking)
                    if (parseFloat(line.net) < 0) return true;

                    // Exclude lines with 0 net amount (no payment to confirm)
                    if (line.net === 0 || line.net === '0.00' || parseFloat(line.net) === 0) {
                        return false;
                    }

                    return true;
                });
                
                // Automatically set status to 'unpaid' for lines that should be locked
                // Excluded lines and negative-net lines: locked as unpaid with prefilled notes
                const linesWithAutoStatus = filteredLines.map(line => {
                    // Excluded lines: prefill notes from exclusion_reason, lock as unpaid
                    const isExcluded = line.payment_status === 'excluded';
                    if (isExcluded) {
                        return {
                            ...line,
                            payment_status: 'unpaid',
                            payment_notes: line.payment_notes || line.exclusion_reason || 'Exclu du lot',
                            isExcluded: true,
                            isLocked: true
                        };
                    }

                    // Negative-net lines (deductions exceeded balance): lock as unpaid
                    if (parseFloat(line.net) < 0) {
                        return {
                            ...line,
                            payment_status: 'unpaid',
                            payment_notes: line.payment_notes || 'Déduction non recouvrée - reportée au lot suivant',
                            isNegativeDeduction: true,
                            isLocked: true
                        };
                    }

                    return line;
                });

                // Partially excluded lines: show two rows — (1) excluded amount (locked unpaid), (2) payable amount (normal)
                const expanded = [];
                for (const line of linesWithAutoStatus) {
                    const excludedAmount = getExcludedSurveyAmountFromLine(line);
                    if (excludedAmount > 0) {
                        // F2F retenue: held as a guarantee at confirmation (not carried); classic exclusions are carried
                        const hasRetenue = (line.adjustments || []).some(adj => adj.type === 'survey_exclusion' && adj.kind === 'retenue');
                        const hasFullSurveyExclusion = (line.adjustments || []).some(adj => adj.type === 'survey_exclusion' && adj.kind !== 'retenue');
                        expanded.push({
                            id: `excluded-${line.id}`,
                            parentLineId: line.id,
                            net: excludedAmount,
                            isExcludedAmountRow: true,
                            excludedAmountLabel: hasRetenue && hasFullSurveyExclusion
                                ? 'Montant exclu (report au prochain lot) / retenue de garantie'
                                : hasRetenue ? "Retenue de garantie (gardée jusqu'à la fin du travail)" : 'Montant exclu (report au prochain lot)',
                            payment_status: 'unpaid',
                            isExcluded: true,
                            payment_notes: line.exclusion_reason || (hasRetenue && !hasFullSurveyExclusion ? "Retenue de garantie (gardée jusqu'à la fin du travail)" : 'Montant exclu (report au prochain lot)'),
                            person_name: line.person_name,
                            person_email: line.person_email,
                            person_ref: line.person_ref,
                            payment_name: line.payment_name,
                            payment_method_type: line.payment_method_type,
                            bank_name: line.bank_name,
                            transfer_service: line.transfer_service,
                            adjustments: line.adjustments,
                        });
                    }
                    expanded.push(line);
                }

                // Overlay saved confirmation progress (draft) onto the lines, skipping locked/virtual rows.
                const draft = batch.confirmation_draft || {};
                const withDraft = expanded.map(line => {
                    if (line.isLocked || line.isExcludedAmountRow) return line;
                    const d = draft[String(line.id)];
                    if (d) {
                        return {
                            ...line,
                            // Restore status if one was chosen; otherwise keep the line's current status.
                            payment_status: (d.status && d.status !== 'pending') ? d.status : line.payment_status,
                            // Restore the note whether or not a status was chosen.
                            payment_notes: d.notes != null ? d.notes : (line.payment_notes ?? ''),
                        };
                    }
                    return line;
                });

                setPaymentLines(withDraft);
                setLastSavedAt(batch.confirmation_draft_saved_at || null);
                setHasUnsavedChanges(false);

                // Auto-load proof files for lines that can have them (Payé/Annulé), so proofs
                // uploaded before leaving show immediately on return.
                withDraft
                    .filter(l => typeof l.id === 'number' && ['paid', 'rejected'].includes(l.payment_status))
                    .forEach(l => fetchProofFiles(l.id));
            } else {
                setError('Erreur lors du chargement du lot de paiement');
            }
        } catch (error) {
            console.error('Error fetching payment batch:', error);
            setError('Erreur lors du chargement du lot de paiement');
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (lineId, status) => {
        // Virtual row for excluded amount cannot be changed
        if (typeof lineId === 'string' && lineId.startsWith('excluded-')) return;
        // Prevent changes if batch is already confirmed
        if (paymentBatch?.status === 'confirmed') {
            setError('Ce lot de paiement a déjà été confirmé et ne peut plus être modifié');
            return;
        }
        
        const previousLine = paymentLines.find(line => line.id === lineId);
        
        // Check if line is unpaid with NET < 200 (locked, cannot be changed)
        // UNLESS force_include_low_amounts (Soldé) is active
        // NET includes total + adjustments (carryover, bonuses, etc.)
        const netAmount = previousLine?.net || 0;
        const isUnpaidUnderThreshold = previousLine?.payment_status === 'unpaid' && netAmount < 200;
        const isSoldéActive = paymentBatch?.force_include_low_amounts ?? false;
        
        // Prevent changes to unpaid lines with total < 200 ONLY if Soldé is not active
        if (isUnpaidUnderThreshold && !isSoldéActive) {
            setError('Les lignes avec un montant net inférieur à 200 MAD sont automatiquement marquées comme non payées et ne peuvent pas être modifiées');
            return;
        }
        
        // Excluded lines: locked to unpaid (same as <200), notes already prefilled
        if (previousLine?.isExcluded) {
            setError('Les lignes exclues du lot sont marquées comme non payées et ne peuvent pas être modifiées');
            return;
        }
        
        const wasPaid = previousLine?.payment_status === 'paid';
        const isNowPaid = status === 'paid';

        setHasUnsavedChanges(true);

        console.log('Status change:', { lineId, status, wasPaid, isNowPaid, previousStatus: previousLine?.payment_status });
        console.log('All payment lines:', paymentLines.map(line => ({ id: line.id, status: line.payment_status })));
        
        // If changing from paid to non-paid, show confirmation modal
        if (wasPaid && !isNowPaid) {
            console.log('Showing confirmation modal for file deletion');
            setPendingStatusChange({ lineId, status });
            setShowDeleteConfirmationModal(true);
        } else {
            console.log('Direct status change');
            // Direct status change for other cases
            setPaymentLines(prevLines => 
                prevLines.map(line => 
                    line.id === lineId 
                        ? { ...line, payment_status: status, payment_notes: line.payment_notes || '' }
                        : line
                )
            );
        }
    };

    const handleNotesChange = (lineId, notes) => {
        setHasUnsavedChanges(true);
        setPaymentLines(prevLines =>
            prevLines.map(line =>
                line.id === lineId
                    ? { ...line, payment_notes: notes }
                    : line
            )
        );
    };

    // Save in-progress choices (status + notes) without confirming, so progress survives leaving the page.
    const handleSaveProgress = async () => {
        if (paymentBatch?.status === 'confirmed') return;
        try {
            setSavingProgress(true);
            setError('');
            // Save a line if it has a chosen status OR a note (so note-only lines persist too).
            const payload = paymentLines
                .filter(l => typeof l.id === 'number' && (
                    (l.payment_status && l.payment_status !== 'pending') ||
                    (l.payment_notes && l.payment_notes.trim())
                ))
                .map(l => ({ id: l.id, status: l.payment_status || 'pending', notes: l.payment_notes || '' }));
            const response = await Api.post(`/payments/${id}/save-confirmation-progress`, { payment_lines: payload });
            if (response.data.success) {
                // Feedback via the inline "Progrès enregistré à …" indicator (no intrusive modal).
                setLastSavedAt(response.data.saved_at || new Date().toISOString());
                setHasUnsavedChanges(false);
            } else {
                setError(response.data.message || 'Erreur lors de l\'enregistrement du progrès');
            }
        } catch (error) {
            console.error('Save progress error:', error);
            setError(error.response?.data?.message || 'Erreur lors de l\'enregistrement du progrès');
        } finally {
            setSavingProgress(false);
        }
    };

    const handleSave = async () => {
        // Prevent saving if batch is already confirmed
        if (paymentBatch?.status === 'confirmed') {
            setError('Ce lot de paiement a déjà été confirmé et ne peut plus être modifié');
            return;
        }
        
        try {
            setSaving(true);
            setError('');

            // Check for unmarked lines before sending (virtual excluded-amount rows are always "marked" unpaid)
            const unmarkedLines = paymentLines.filter(line => {
                if (line.isExcludedAmountRow) return false; // Virtual row: no status to mark
                // Check if line should be locked
                const hasDeletionDeduction = line.adjustments && Array.isArray(line.adjustments) && 
                    line.adjustments.some(adj => adj.type === 'deleted_survey_deduction');
                const isDeletionOnly = line.detail?.is_deletion_only === true || 
                    (hasDeletionDeduction && line.qty === 0 && line.total === 0);
                
                // Only skip validation if:
                // 1. Net amount is negative (they owe money), OR
                // 2. It's a deletion-only line (net = 0, qty = 0, total = 0)
                const shouldLock = (line.net < 0) || (isDeletionOnly && line.net <= 0);
                if (shouldLock) return false;
                
                return !line.payment_status || line.payment_status === 'pending' || line.payment_status === '';
            });

            if (unmarkedLines.length > 0) {
                setError('Veuillez marquer le statut de tous les paiements avant de confirmer');
                // Scroll to first unmarked line
                const firstUnmarkedId = unmarkedLines[0].id;
                const element = document.querySelector(`tr[data-line-id="${firstUnmarkedId}"]`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    element.style.backgroundColor = '#fef2f2';
                    setTimeout(() => {
                        element.style.backgroundColor = '';
                    }, 3000);
                }
                return;
            }

            // Check if all paid lines have proof files
            const allPaidLinesHaveProofFiles = await checkPaidLinesHaveProofFiles();
            if (!allPaidLinesHaveProofFiles) {
                setError('Les lignes marquées comme "Payé" doivent avoir au moins un fichier de preuve avant de confirmer');
                return;
            }

            // "Annuler" (rejected) requires a mandatory justification note
            const cancelledWithoutReason = paymentLines.filter(line =>
                line.payment_status === 'rejected' && !(line.payment_notes && line.payment_notes.trim())
            );
            if (cancelledWithoutReason.length > 0) {
                setError('Une justification (note) est obligatoire pour chaque ligne annulée.');
                const el = document.querySelector(`tr[data-line-id="${cancelledWithoutReason[0].id}"]`);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.style.backgroundColor = '#fef2f2';
                    setTimeout(() => { el.style.backgroundColor = ''; }, 3000);
                }
                return;
            }

            // Prepare payment data for all lines (including 0-amount lines that were filtered from display)
            // Get all lines from the batch, not just the filtered ones
            const allBatchLines = paymentBatch?.lines || [];
            
            const paymentData = allBatchLines.map(line => {
                // Check if it's a deletion-only line
                const hasDeletionDeduction = line.adjustments && Array.isArray(line.adjustments) && 
                    line.adjustments.some(adj => adj.type === 'deleted_survey_deduction');
                const isDeletionOnly = line.detail?.is_deletion_only === true || 
                    (hasDeletionDeduction && line.qty === 0 && line.total === 0);
                
                // Check if line is unpaid with NET < 200 (locked, cannot be changed)
                // UNLESS force_include_low_amounts (Soldé) is active
                // NET includes total + adjustments (carryover, bonuses, etc.)
                const netAmount = line.net || 0;
                const isUnpaidUnderThreshold = line.payment_status === 'unpaid' && netAmount < 200;
                const isSoldéActive = paymentBatch?.force_include_low_amounts ?? false;
                
                // Check if line has 0 net amount (no payment needed)
                const hasZeroAmount = line.net === 0 || line.net === '0.00' || parseFloat(line.net) === 0;
                
                // Automatically mark 0-amount lines as paid (no payment to confirm)
                if (hasZeroAmount && !isDeletionOnly) {
                    return {
                        id: line.id,
                        status: 'paid',
                        notes: line.payment_notes || 'Montant zéro - marqué automatiquement comme payé'
                    };
                }
                
                // For unpaid lines with total < 200, keep them as unpaid (locked) ONLY if Soldé is not active
                // If Soldé is active, allow status to be changed
                if (isUnpaidUnderThreshold && !isSoldéActive) {
                    return {
                        id: line.id,
                        status: 'unpaid',
                        notes: line.payment_notes || 'Montant total inférieur à 200 MAD'
                    };
                }
                
                // Excluded lines: same as <200 — status unpaid, notes prefilled
                if (line.payment_status === 'excluded') {
                    const displayedLine = paymentLines.find(l => l.id === line.id);
                    return {
                        id: line.id,
                        status: displayedLine?.payment_status || 'unpaid',
                        notes: displayedLine?.payment_notes || line.exclusion_reason || line.payment_notes || 'Exclu du lot'
                    };
                }
                
                // For displayed lines, use their current status
                const displayedLine = paymentLines.find(l => l.id === line.id);
                if (displayedLine) {
                    // Check if line should be locked
                    const shouldLock = (line.net < 0) || (isDeletionOnly && line.net <= 0);
                    
                    // Automatically set to 'unpaid' if it's a locked line and status is not set or pending
                    let status = displayedLine.payment_status;
                    if (shouldLock && (!status || status === 'pending')) {
                        status = 'unpaid';
                    }
                    
                    return {
                        id: line.id,
                        status: status,
                        notes: displayedLine.payment_notes || ''
                    };
                }
                
                // For lines not displayed (deletion-only), keep their existing status or set to unpaid
                const shouldLock = (line.net < 0) || (isDeletionOnly && line.net <= 0);
                return {
                    id: line.id,
                    status: shouldLock ? 'unpaid' : (line.payment_status || 'pending'),
                    notes: line.payment_notes || ''
                };
            });

            const response = await Api.post(`/payments/${id}/confirm-payment-status`, {
                payment_lines: paymentData
            });

            if (response.data.success) {
                let successMessage = response.data.message;
                
                // Add carryover information if available
                if (response.data.data?.carryover_data?.carryover_created) {
                    const carryover = response.data.data.carryover_data;
                    successMessage += `\n\nReport automatique activé: ${carryover.total_amount.toFixed(2)} MAD pour ${carryover.interviewers ? Object.keys(carryover.interviewers).length : 0} enquêteur(s) sera reporté au prochain lot.`;
                }
                
                setHasUnsavedChanges(false);
                // The batch is confirmed now: the page's guards block any second confirm / save-progress
                setPaymentBatch(prev => (prev ? { ...prev, status: 'confirmed' } : prev));
                setSuccessMessage(successMessage);
                setShowSuccessModal(true);
            } else {
                setError(response.data.message || 'Erreur lors de la confirmation');
            }
        } catch (error) {
            console.error('Error confirming payment status:', error);
            
            // Handle specific error responses from backend
            if (error.response?.data) {
                const errorData = error.response.data;
                
                if (errorData.error_type === 'unmarked_lines' && errorData.unmarked_lines) {
                    setError(errorData.message);
                    
                    // Scroll to first unmarked line and highlight it
                    const firstUnmarked = errorData.unmarked_lines[0];
                    const element = document.querySelector(`tr[data-line-id="${firstUnmarked.id}"]`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        element.style.backgroundColor = '#fef2f2';
                        setTimeout(() => {
                            element.style.backgroundColor = '';
                        }, 5000);
                    }
                } else if (errorData.error_type === 'missing_proof_files' && errorData.lines_without_proof) {
                    setError(errorData.message);
                    
                    // Scroll to first line without proof and highlight it
                    const firstWithoutProof = errorData.lines_without_proof[0];
                    const element = document.querySelector(`tr[data-line-id="${firstWithoutProof.id}"]`);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        element.style.backgroundColor = '#fef2f2';
                        setTimeout(() => {
                            element.style.backgroundColor = '';
                        }, 5000);
                    }
                } else {
                    setError(errorData.message || 'Erreur lors de la confirmation du statut de paiement');
                }
            } else {
                setError('Erreur lors de la confirmation du statut de paiement');
            }
        } finally {
            setSaving(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'paid':
                return <FontAwesomeIcon icon={faCheck} className="text-green-600" />;
            case 'unpaid':
                return <FontAwesomeIcon icon={faTimes} className="text-red-600" />;
            case 'rejected':
                return <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600" />;
            case 'insufficient':
                return <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-600" />;
            default:
                return <span className="text-gray-400">-</span>;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'paid':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'unpaid':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'rejected':
                return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'insufficient':
                return 'bg-orange-100 text-orange-800 border-orange-200';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'paid':
                return 'Payé';
            case 'unpaid':
                return 'Non payé';
            case 'rejected':
                return 'Annulé';
            case 'insufficient':
                return 'Insuffisant';
            default:
                return 'En attente';
        }
    };

    const canSave = paymentLines.some(line => 
        line.payment_status && line.payment_status !== 'pending'
    );

    // Check if all paid lines have proof files
    // Exclude 0-amount lines from proof file requirement (they're auto-marked as paid)
    const checkPaidLinesHaveProofFiles = async () => {
        const paidLines = paymentLines.filter(line => {
            if (line.payment_status !== 'paid' || line.isExcludedAmountRow) return false;
            const hasZeroAmount = line.net === 0 || line.net === '0.00' || parseFloat(line.net) === 0;
            return !hasZeroAmount;
        });
        
        console.log('Checking proof files for paid lines (excluding 0-amount):', paidLines.length);
        
        if (paidLines.length === 0) return true;

        for (const line of paidLines) {
            try {
                const response = await Api.get(`/payments/${id}/lines/${line.id}/proof-files`);
                console.log(`Line ${line.id} proof files:`, response.data.files?.length || 0);
                if (response.data.success && response.data.files && response.data.files.length === 0) {
                    console.log(`Line ${line.id} has no proof files`);
                    return false; // This paid line has no proof files
                }
            } catch (error) {
                console.error('Error checking proof files for line:', line.id, error);
                return false; // Assume no files if error
            }
        }
        console.log('All paid lines have proof files');
        return true; // All paid lines have proof files
    };

    // File upload functions
    const handleFileUpload = async (lineId, files) => {
        // Accept a single file, a FileList, or an array — upload them all sequentially.
        const fileList = files ? (Array.isArray(files) ? files : Array.from(files.length !== undefined ? files : [files])) : [];
        if (fileList.length === 0) return;

        // Find the payment line to check its status. Proof upload is allowed for paid lines
        // (obligatory) and cancelled/annulé lines (optional).
        const paymentLine = paymentLines.find(line => line.id === lineId);
        if (!paymentLine || !['paid', 'rejected'].includes(paymentLine.payment_status)) {
            setError('Vous ne pouvez téléverser des fichiers que pour les lignes "Payé" ou "Annulé"');
            return;
        }

        setUploadingFiles(prev => ({ ...prev, [lineId]: true }));

        let ok = 0;
        let failed = 0;
        try {
            for (const file of fileList) {
                try {
                    const formData = new FormData();
                    formData.append('file', file);
                    const response = await Api.post(`/payments/${id}/lines/${lineId}/upload-proof`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    });
                    if (response.data.success) { ok++; } else { failed++; }
                } catch (err) {
                    console.error('Upload error:', err);
                    failed++;
                }
            }

            await fetchProofFiles(lineId);

            if (failed === 0) {
                setSuccessMessage(ok > 1 ? `${ok} fichiers téléversés avec succès` : 'Fichier téléversé avec succès');
            } else if (ok > 0) {
                setError(`${ok} fichier(s) téléversé(s), ${failed} échec(s)`);
            } else {
                setError('Erreur lors du téléversement des fichiers');
            }
        } finally {
            setUploadingFiles(prev => ({ ...prev, [lineId]: false }));
        }
    };

    const fetchProofFiles = async (lineId) => {
        try {
            const response = await Api.get(`/payments/${id}/lines/${lineId}/proof-files`);
            if (response.data.success) {
                setProofFiles(prev => ({
                    ...prev,
                    [lineId]: response.data.files
                }));
            }
        } catch (error) {
            console.error('Error fetching proof files:', error);
        }
    };

    const handleFileDelete = async (lineId, fileId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce fichier ?')) {
            return;
        }

        try {
            const response = await Api.delete(`/payments/${id}/lines/${lineId}/proof-files/${fileId}`);
            if (response.data.success) {
                await fetchProofFiles(lineId);
                setSuccessMessage('Fichier supprimé avec succès');
            } else {
                setError(response.data.message || 'Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('Delete error:', error);
            setError(error.response?.data?.message || 'Erreur lors de la suppression du fichier');
        }
    };

    const handleFileDownload = async (fileId) => {
        try {
            const response = await Api.get(`/payments/proof-files/${fileId}/download`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'proof-file');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
            setError('Erreur lors du téléchargement du fichier');
        }
    };

    const getFileIcon = (fileType) => {
        if (fileType === 'pdf') return faFilePdf;
        if (['doc', 'docx'].includes(fileType)) return faFileWord;
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileType)) return faImage;
        if (['csv', 'xls', 'xlsx'].includes(fileType)) return faFileExcel; // Excel/CSV files
        return faFileAlt;
    };

    const handleConfirmStatusChange = async () => {
        console.log('handleConfirmStatusChange called');
        if (!pendingStatusChange) {
            console.log('No pending status change');
            return;
        }
        
        const { lineId, status } = pendingStatusChange;
        console.log('Confirming status change:', { lineId, status });
        
        try {
            // First, get all proof files for this line
            console.log('Fetching proof files for line:', lineId);
            console.log('API URL:', `/payments/${id}/lines/${lineId}/proof-files`);
            const response = await Api.get(`/payments/${id}/lines/${lineId}/proof-files`);
            console.log('Proof files response:', response.data);
            
            if (response.data.success && response.data.files && response.data.files.length > 0) {
                console.log(`Found ${response.data.files.length} files to delete`);
                
                for (const file of response.data.files) {
                    try {
                        console.log('Deleting file:', file.id, file.original_filename);
                        console.log('Delete API URL:', `/payments/${id}/lines/${lineId}/proof-files/${file.id}`);
                        const deleteResponse = await Api.delete(`/payments/${id}/lines/${lineId}/proof-files/${file.id}`);
                        console.log('Delete response:', deleteResponse.data);
                    } catch (deleteError) {
                        console.error('Error deleting file:', file.id, deleteError);
                        console.error('Delete error details:', deleteError.response?.data);
                        setError(`Erreur lors de la suppression du fichier ${file.original_filename}`);
                        return;
                    }
                }
                console.log('All files deleted successfully');
            } else {
                console.log('No files found to delete');
            }
            
            // Update the status
            setPaymentLines(prevLines => 
                prevLines.map(line => 
                    line.id === lineId 
                        ? { ...line, payment_status: status, payment_notes: line.payment_notes || '' }
                        : line
                )
            );
            
            // Clear proof files from local state
            setProofFiles(prev => ({
                ...prev,
                [lineId]: []
            }));
            
            setSuccessMessage('Statut modifié et fichiers supprimés avec succès');
            setShowSuccessModal(true);
            
        } catch (error) {
            console.error('Error confirming status change:', error);
            setError('Erreur lors de la modification du statut');
        } finally {
            setShowDeleteConfirmationModal(false);
            setPendingStatusChange(null);
        }
    };

    const handleCancelStatusChange = () => {
        setShowDeleteConfirmationModal(false);
        setPendingStatusChange(null);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
        );
    }


    if (!paymentBatch) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-600">Lot de paiement non trouvé</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <button
                            onClick={() => navigate('/payments')}
                            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                            Retour aux paiements
                        </button>
                        <h1 className="text-2xl font-bold text-gray-900">
                            Confirmation des Paiements
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Lot #{paymentBatch.id} - {new Date(paymentBatch.period_start).toLocaleDateString('fr-FR')} à {new Date(paymentBatch.period_end).toLocaleDateString('fr-FR')}
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-sm text-gray-600">Statut du lot</div>
                        <div className="text-lg font-semibold text-gray-900">
                            {paymentBatch.status === 'exported' ? 'Exporté' : paymentBatch.status}
                        </div>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {/* Floating under the top navbar: visible wherever the page is scrolled (e.g. when it scrolls
                to the line the error is about). Not `sticky`: the layout's overflow-hidden wrapper disables it. */}
            {error && (
                <div role="alert" className="fixed top-20 left-4 right-4 lg:left-auto lg:right-6 lg:w-[34rem] z-40 bg-red-50 border border-red-300 rounded-lg p-4 shadow-lg">
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-2 flex-shrink-0" />
                        <span className="text-red-800 flex-1">{error}</span>
                        <button onClick={() => setError('')} className="ml-3 text-red-400 hover:text-red-700" title="Fermer">
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>
            )}


            {/* Payment Lines */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-gray-900">
                            Lignes de Paiement ({paymentLines.length})
                        </h2>
                        {paymentBatch && paymentBatch.lines && paymentLines.length < paymentBatch.lines.length && (
                            <div className="text-sm text-gray-600">
                                <span className="text-blue-600 font-medium">
                                    {paymentBatch.lines.length - paymentLines.length}
                                </span>
                                {' '}ligne(s) de déduction pour enquêtes supprimées exclue(s) (déjà enregistrée(s) comme non payée(s))
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                                    Enquêteur
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                                    Bénéficiaire
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                    Montant
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                                    Méthode
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                                    Statut Actuel
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                                    Confirmer Statut
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[300px]">
                                    Notes
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                                    Preuves de Paiement
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paymentLines.map((line) => (
                                <tr key={line.id} data-line-id={line.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap w-48">
                                        <div className="flex items-center">
                                            <FontAwesomeIcon icon={faUser} className="text-gray-400 mr-2" />
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {line.person_name || 'N/A'}
                                                </div>
                                                <div className="text-xs text-gray-400">
                                                    Ref: {line.person_ref || 'N/A'}
                                                </div>
                                                {!line.isExcludedAmountRow && typeof line.id === 'number' && (
                                                    <button
                                                        onClick={() => navigate(`/payments/${id}/lines/${line.id}`)}
                                                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                                                    >
                                                        Voir détails
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap w-40">
                                        <span className="text-sm text-gray-900">
                                            {line.isExcludedAmountRow ? (line.excludedAmountLabel || 'Montant exclu (report au prochain lot)') : (line.payment_name || '-')}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap w-24">
                                        <span className={`text-sm font-medium ${line.isExcludedAmountRow ? 'text-amber-700' : 'text-gray-900'}`}>
                                            {line.net != null ? parseFloat(line.net).toFixed(2) : '0.00'} MAD
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap w-32">
                                        <div>
                                            <div className="text-sm text-gray-900">
                                                {line.payment_method_type === 'bank' ? 'Banque' : 'Transfert'}
                                            </div>
                                            {line.payment_method_type === 'bank' && line.bank_name && (
                                                <div className="text-xs text-gray-500">
                                                    {line.bank_name}
                                                </div>
                                            )}
                                            {line.payment_method_type === 'transfer' && line.transfer_service && (
                                                <div className="text-xs text-gray-500">
                                                    {line.transfer_service}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap w-32">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(line.payment_status || 'pending')}`}>
                                            {getStatusIcon(line.payment_status || 'pending')}
                                            <span className="ml-1">{getStatusText(line.payment_status || 'pending')}</span>
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap w-48">
                                        {(() => {
                                            // Check if batch is already confirmed - disable all status changes
                                            const isBatchConfirmed = paymentBatch?.status === 'confirmed';

                                            // Express (advance-only) batches: no "Non payé" — an express advance must be
                                            // resolved now (Payé ou Annulé), never deferred/reporté to a future batch.
                                            const isExpress = paymentBatch?.creation_mode === 'express';

                                            // "Annuler" (reject) is only offered on advance-bearing lines (frais de
                                            // déplacement). Cancelling one rejects the linked advance at confirmation.
                                            const isAdvanceLine = Array.isArray(line.adjustments)
                                                && line.adjustments.some(a => a?.type === 'advance_frais_deplacement');

                                            // Check if line has deletion deductions
                                            const hasDeletionDeduction = line.adjustments && Array.isArray(line.adjustments) && 
                                                line.adjustments.some(adj => adj.type === 'deleted_survey_deduction');
                                            
                                            // Check if it's a deletion-only line (no work, only deductions)
                                            const isDeletionOnly = line.detail?.is_deletion_only === true || 
                                                (hasDeletionDeduction && line.qty === 0 && line.total === 0);
                                            
                                            // Check if line is unpaid with NET < 200 (locked, cannot be changed)
                                            // UNLESS force_include_low_amounts (Soldé) is active
                                            // NET includes total + adjustments (carryover, bonuses, etc.)
                                            const netAmount = line.net || 0;
                                            const isUnpaidUnderThreshold = line.payment_status === 'unpaid' && netAmount < 200;
                                            const isSoldéActive = paymentBatch?.force_include_low_amounts ?? false;
                                            const isExcludedLine = line.isExcluded === true;
                                            const isNegativeDeduction = line.isNegativeDeduction === true || parseFloat(line.net) < 0;

                                            // Only lock if:
                                            // 1. Batch is already confirmed, OR
                                            // 2. Net amount is negative (deductions exceeded balance), OR
                                            // 3. It's unpaid with total < 200 AND Soldé is NOT active, OR
                                            // 4. It's an excluded line (notes prefilled, status locked to unpaid)
                                            const shouldLock = isBatchConfirmed || isNegativeDeduction || (isUnpaidUnderThreshold && !isSoldéActive) || isExcludedLine;

                                            // If should lock, lock to unpaid
                                            if (shouldLock) {
                                                // Excluded, negative deduction, or unpaid with NET < 200: lock to unpaid
                                                if ((isExcludedLine || isNegativeDeduction || (isUnpaidUnderThreshold && !isSoldéActive))) {
                                                    return (
                                                        <div className="flex space-x-2">
                                                            <button
                                                                disabled
                                                                className="px-3 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                                                            >
                                                                Payé
                                                            </button>
                                                            <button
                                                                disabled
                                                                className="px-3 py-1 text-xs font-medium rounded-full border bg-red-100 text-red-800 border-red-200 cursor-not-allowed"
                                                                title={isNegativeDeduction ? "Déduction non recouvrée - reportée au lot suivant" : "Montant net inférieur à 200 MAD - statut verrouillé automatiquement"}
                                                            >
                                                                Non Payé
                                                            </button>
                                                            {isAdvanceLine && (
                                                                <button
                                                                    disabled
                                                                    className="px-3 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                                                                >
                                                                    Annuler
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                
                                                // For deletion-only lines, lock to unpaid
                                                return (
                                                    <div className="flex space-x-2">
                                                        <button
                                                            disabled
                                                            className="px-3 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                                                        >
                                                            Payé
                                                        </button>
                                                        <button
                                                            className="px-3 py-1 text-xs font-medium rounded-full border bg-red-100 text-red-800 border-red-200"
                                                        >
                                                            Non Payé
                                                        </button>
                                                        {isAdvanceLine && (
                                                            <button
                                                                disabled
                                                                className="px-3 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50"
                                                            >
                                                                Annuler
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            }
                                            
                                            // Normal status buttons for lines without deletion deductions
                                            // Disable all buttons if batch is already confirmed
                                            return (
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleStatusChange(line.id, 'paid')}
                                                        disabled={isBatchConfirmed}
                                                        className={`px-3 py-1 text-xs font-medium rounded-full border ${
                                                            isBatchConfirmed
                                                                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                                                                : line.payment_status === 'paid'
                                                                ? 'bg-green-100 text-green-800 border-green-200'
                                                                : 'bg-white text-green-600 border-green-200 hover:bg-green-50'
                                                        }`}
                                                        title={isBatchConfirmed ? 'Ce lot a déjà été confirmé' : ''}
                                                    >
                                                        Payé
                                                    </button>
                                                    {!isExpress && (
                                                        <button
                                                            onClick={() => handleStatusChange(line.id, 'unpaid')}
                                                            disabled={isBatchConfirmed}
                                                            className={`px-3 py-1 text-xs font-medium rounded-full border ${
                                                                isBatchConfirmed
                                                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                                                                    : line.payment_status === 'unpaid'
                                                                    ? 'bg-red-100 text-red-800 border-red-200'
                                                                    : 'bg-white text-red-600 border-red-200 hover:bg-red-50'
                                                            }`}
                                                            title={isBatchConfirmed ? 'Ce lot a déjà été confirmé' : ''}
                                                        >
                                                            Non Payé
                                                        </button>
                                                    )}
                                                    {isAdvanceLine && (
                                                        <button
                                                            onClick={() => handleStatusChange(line.id, 'rejected')}
                                                            disabled={isBatchConfirmed}
                                                            className={`px-3 py-1 text-xs font-medium rounded-full border ${
                                                                isBatchConfirmed
                                                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed opacity-50'
                                                                    : line.payment_status === 'rejected'
                                                                    ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                                                                    : 'bg-white text-yellow-600 border-yellow-200 hover:bg-yellow-50'
                                                            }`}
                                                            title={isBatchConfirmed ? 'Ce lot a déjà été confirmé' : 'Annuler cette avance (rejette l\'avance de frais de déplacement)'}
                                                        >
                                                            Annuler
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-6 py-4 min-w-[300px]">
                                        {line.payment_status === 'rejected' && !(line.payment_notes && line.payment_notes.trim()) && (
                                            <div className="text-xs text-red-600 font-medium mb-1">
                                                Justification obligatoire pour une annulation
                                            </div>
                                        )}
                                        <textarea
                                            value={line.payment_notes || ''}
                                            onChange={(e) => handleNotesChange(line.id, e.target.value)}
                                            placeholder={line.payment_status === 'rejected' ? 'Motif de l\'annulation (obligatoire)…' : 'Notes optionnelles...'}
                                            className={`w-full min-w-[300px] px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
                                                line.payment_status === 'rejected' && !(line.payment_notes && line.payment_notes.trim())
                                                    ? 'border-red-400 bg-red-50'
                                                    : 'border-gray-300'
                                            }`}
                                            rows="3"
                                        />
                                    </td>
                                    <td className="px-6 py-4 w-48">
                                        <div className="space-y-3">
                                            {/* File Upload - paid (obligatoire) + rejected/annulé (optionnel) */}
                                            {(line.payment_status === 'paid' || line.payment_status === 'rejected') && (
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="file"
                                                        id={`file-${line.id}`}
                                                        multiple
                                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.bmp,.webp,.csv,.xls,.xlsx"
                                                        onChange={(e) => { const files = Array.from(e.target.files); e.target.value = ''; handleFileUpload(line.id, files); }}
                                                        className="hidden"
                                                    />
                                                    <label
                                                        htmlFor={`file-${line.id}`}
                                                        className="flex items-center px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100 cursor-pointer relative group"
                                                    >
                                                        {uploadingFiles[line.id] ? (
                                                            <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-1" />
                                                        ) : (
                                                            <FontAwesomeIcon icon={faUpload} className="mr-1" />
                                                        )}
                                                        {uploadingFiles[line.id] ? 'Upload...' : 'Ajouter'}
                                                        
                                                        {/* Tooltip */}
                                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                            <div className="text-center">
                                                                <div className="font-semibold mb-1">Types de fichiers acceptés:</div>
                                                                <div className="text-xs">
                                                                    PDF, DOC, DOCX, JPG, JPEG, PNG,<br/>
                                                                    GIF, BMP, WEBP, CSV, XLS, XLSX
                                                                </div>
                                                                <div className="mt-1 font-semibold">Taille maximum: 10MB</div>
                                                            </div>
                                                            {/* Arrow */}
                                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                                        </div>
                                                    </label>
                                                    <button
                                                        onClick={() => fetchProofFiles(line.id)}
                                                        className="flex items-center px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                                                    >
                                                        <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
                                                        ({proofFiles[line.id]?.length || 0})
                                                    </button>
                                                    {line.payment_status === 'rejected' && (
                                                        <span className="text-xs text-gray-400 italic">optionnel</span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Show message for lines that cannot upload (neither paid nor cancelled) */}
                                            {line.payment_status !== 'paid' && line.payment_status !== 'rejected' && (
                                                <div className="text-xs text-gray-500 italic">
                                                    Marquez comme "Payé" pour téléverser des fichiers
                                                </div>
                                            )}

                                            {/* Proof Files List */}
                                            {proofFiles[line.id] && proofFiles[line.id].length > 0 && (
                                                <div className="space-y-1">
                                                    {proofFiles[line.id].map((file) => (
                                                        <div key={file.id} className="flex items-center justify-between bg-gray-50 rounded p-2">
                                                            <div className="flex items-center space-x-2">
                                                                <FontAwesomeIcon 
                                                                    icon={getFileIcon(file.file_type)} 
                                                                    className="text-gray-500 text-sm" 
                                                                />
                                                                <div className="text-xs">
                                                                    <div className="font-medium text-gray-900 truncate max-w-32">
                                                                        {file.original_filename}
                                                                    </div>
                                                                    <div className="text-gray-500">
                                                                        {file.file_size} • {file.uploaded_at}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center space-x-1">
                                                                <button
                                                                    onClick={() => handleFileDownload(file.id)}
                                                                    className="p-1 text-blue-600 hover:text-blue-800"
                                                                    title="Télécharger"
                                                                >
                                                                    <FontAwesomeIcon icon={faDownload} className="text-xs" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleFileDelete(line.id, file.id)}
                                                                    className="p-1 text-red-600 hover:text-red-800"
                                                                    title="Supprimer"
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Save Button */}
            {canSave && (
                <div className="flex justify-end">
                    {paymentBatch?.status === 'confirmed' ? (
                        <div className="bg-gray-100 text-gray-600 px-6 py-2 rounded-lg flex items-center">
                            <FontAwesomeIcon icon={faCheck} className="mr-2" />
                            Lot déjà confirmé - Modifications désactivées
                        </div>
                    ) : (
                        <div className="flex flex-col items-end gap-2">
                            <div className="text-xs text-gray-500 h-4">
                                {hasUnsavedChanges
                                    ? 'Modifications non enregistrées'
                                    : (lastSavedAt ? `Progrès enregistré à ${new Date(lastSavedAt).toLocaleString('fr-FR')}` : '')}
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleSaveProgress}
                                    disabled={savingProgress || saving}
                                    title="Enregistrer votre progrès sans confirmer — vous pourrez reprendre plus tard"
                                    className="bg-white border border-gray-300 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                    {savingProgress ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                                            Enregistrement...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} className="mr-2" />
                                            Enregistrer le progrès
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving || savingProgress}
                                    className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                >
                                    {saving ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Confirmation...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faCheck} className="mr-2" />
                                            Confirmer les Statuts
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center mb-4">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mr-3 text-xl" />
                            <h3 className="text-lg font-semibold text-gray-900">
                                Confirmer la suppression des fichiers
                            </h3>
                        </div>
                        
                        <div className="mb-6">
                            <p className="text-gray-700 mb-3">
                                Vous êtes sur le point de changer le statut de "Payé" à "{pendingStatusChange?.status === 'unpaid' ? 'Non Payé' : 'Rejeté'}".
                            </p>
                            <p className="text-red-600 font-medium">
                                ⚠️ Cette action supprimera définitivement tous les fichiers de preuve uploadés pour cette ligne.
                            </p>
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={handleCancelStatusChange}
                                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleConfirmStatusChange}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                                Confirmer et Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    // Only redirect to payment batch detail if it's a batch confirmation, not a status change
                    if (successMessage.includes('Statut confirmé pour')) {
                        navigate(`/payments/${id}`);
                    }
                }}
                title="Confirmation Réussie"
                message={successMessage}
            />
        </div>
    );
};

export default PaymentConfirmation;
